/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';
import { DayPickerSingleDateController, isSameDay } from 'react-dates';
import 'react-dates/initialize';

import { times } from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment-timezone';

import timezone from 'js/lib/timezone';
import user from 'js/lib/user';
import { DAY, LONG_MONTH_YEAR_DISPLAY, MED_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography, typography2 } from '@coursera/cds-core';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import { getCalendarKey } from 'bundles/degree-home/components/v3-dpm/components/Calendar/utils';
import DatePickerNavButton from 'bundles/ui/components/DatePicker/DatePickerNavButton';

import _t from 'i18n!nls/degree-home';

// eslint-disable-next-line no-restricted-syntax,import/extensions
import 'css!react-dates/lib/css/_datepicker.css';

import CalendarDay from './CalendarDay';

const CALENDAR_DAY_PICKER_HEIGHT = '350px';

const styles = {
  calendar: css`
    .DayPicker,
    .DayPicker__horizontal {
      width: 100% !important;
      margin: auto;
      visibility: visible;
      background: transparent;
    }

    .DayPicker_transitionContainer {
      width: 100% !important;
      height: ${CALENDAR_DAY_PICKER_HEIGHT} !important;
    }

    .CalendarMonthGrid,
    .CalendarMonthGrid__horizontal {
      width: 100% !important;
      z-index: 2;
      background: transparent;
      left: 0 !important;
    }

    .CalendarMonthGrid_month__horizontal {
      width: 100% !important;
    }

    .DayPicker_weekHeaders,
    .DayPicker_weekHeaders__horizontal {
      top: 50px;
      width: 100%;
      margin-left: 0 !important;
    }

    .DayPicker_weekHeader,
    .DayPicker_weekHeader_ul {
      width: 100% !important;
    }

    .DayPicker_weekHeader_li {
      width: calc(100% / 7) !important;
    }

    .CalendarMonth_table {
      border-spacing: 5px 0;
      border-collapse: separate;
      width: 100% !important;
    }

    .CalendarMonth {
      background: transparent;
      width: 100% !important;
    }

    .CalendarDay {
      border: 0;
      padding-top: 10px;
      white-space: pre;
      line-height: 10px;
      border-radius: 3px;
      color: var(--cds-color-neutral-primary);
      background: transparent;
    }

    .CalendarDay__default:hover {
      background: var(--cds-color-grey-25);
    }

    .CalendarDay__today {
      background: transparent;
      border: 1px solid var(--cds-color-blue-850);
    }

    .CalendarDay__outside {
      color: var(--cds-color-grey-700);
    }

    .CalendarDay__selected {
      background: var(--cds-color-blue-850);
      color: var(--cds-color-neutral-primary-invert);
    }

    .CalendarDay__selected:hover {
      background: var(--cds-color-grey-25);
      color: var(--cds-color-neutral-primary);
    }

    .DayPicker_weekHeaders {
      top: 0;
    }

    .DayPicker_weekHeader {
      ${{ ...typography2.bodyPrimary }}
      color: var(--cds-color-grey-700);
      font-weight: 700;
    }

    .CalendarMonthGrid {
      z-index: 2;
      background: transparent;
    }

    .DayPickerNavigation {
      z-index: 3;
    }

    .DayPicker > div > div {
      width: 100% !important;
      height: ${CALENDAR_DAY_PICKER_HEIGHT} !important;
    }
  `,
  dotSvg: css`
    margin: 0 1px;
  `,
  firstLineDots: css`
    line-height: 10px;
    min-height: 10px;
  `,
  secondLineDots: css`
    line-height: 2px;
    min-height: 6px;
  `,
  screenReaderOnly: css`
    position: absolute;
    top: -2000em;
    left: -3000em;
  `,
};

const dotSvg = (
  <svg className="dot" viewBox="0 0 4 4" height={4} width={4} css={styles.dotSvg}>
    <circle cx={2} cy={2} r={2} />
  </svg>
);

const plusSvg = (
  <svg className="plus" viewBox="0 0 4 4" height={4} width={4} css={styles.dotSvg}>
    <line x1={2} y1={0} x2={2} y2={4} stroke="black" strokeWidth={0.5} />
    <line x1={0} y1={2} x2={4} y2={2} stroke="black" strokeWidth={0.5} />
  </svg>
);

const MAX_NUM_FIRST_LINE_DOTS = 5;
const MAX_NUM_SECOND_LINE_DOTS = 2;

export const CalendarDayPicker = () => {
  const { dateInFocus, setDateInFocus, visibleCalendarEvents } = useDegreeContext();

  // moment and react-dates have a peer dependency
  // by changing moment's timezone, we will also change the timezone used by react-dates
  // this is a workaround because there isn't a way to specify a timezone within react-dates
  useEffect(() => {
    const localTimezone = user.getUserTimezone() || timezone.get();
    moment.tz.setDefault(localTimezone);
  }, []);

  return (
    <div css={styles.calendar} data-testid="calendar-date-picker">
      {/* The purpose of this div is to announce the state of selected date changes on the Calendar Picker for screen readers */}
      <div css={styles.screenReaderOnly} aria-live="assertive" aria-atomic="true">
        {_t('Selected. #{date}. #{isCurrentDate}', {
          date: formatDateTimeDisplay(dateInFocus, MED_DATE_ONLY_DISPLAY),
          isCurrentDate: isSameDay(moment(), dateInFocus) ? _t('Current Date') : '',
        })}
      </div>
      <DayPickerSingleDateController
        date={dateInFocus}
        focused={false} // change to 'false' to avoid reminder banner is being ignored when the calendar card is focused after 'In Progress' tab is loaded
        transitionDuration={0}
        noBorder={true}
        onDateChange={(date: Moment | null) => date && setDateInFocus(date)}
        onFocusChange={() => {}}
        navPrev={<DatePickerNavButton isBack />}
        navNext={<DatePickerNavButton />}
        onPrevMonthClick={(date: Moment) => setDateInFocus(date)}
        onNextMonthClick={(date: Moment) => setDateInFocus(date)}
        enableOutsideDays={true}
        renderCalendarDay={(props) => <CalendarDay {...props} tabIndex={0} />}
        renderDayContents={(day: Moment) => {
          const eventsToShow = visibleCalendarEvents?.[getCalendarKey(day)]?.length || 0;
          const isSelectedDate = day.isSame(dateInFocus, 'day');
          const firstLineDots = times(Math.min(eventsToShow, MAX_NUM_FIRST_LINE_DOTS), () => dotSvg);
          const secondLineDots =
            eventsToShow - MAX_NUM_FIRST_LINE_DOTS > 0 &&
            times(Math.min(eventsToShow - MAX_NUM_FIRST_LINE_DOTS, MAX_NUM_SECOND_LINE_DOTS), () => dotSvg);
          const secondLinePlus = eventsToShow > MAX_NUM_FIRST_LINE_DOTS + MAX_NUM_SECOND_LINE_DOTS && plusSvg;
          return (
            <div className="calendar-day-contents">
              <div>{day.format(DAY)}</div>
              {/* Don't show dots on selected date. We keep the div elements to maintain proper line height. */}
              <div css={styles.firstLineDots}>{!isSelectedDate && firstLineDots}</div>
              <div css={styles.secondLineDots}>
                {!isSelectedDate && secondLineDots}
                {!isSelectedDate && secondLinePlus}
              </div>
            </div>
          );
        }}
        renderMonthElement={({ month }: $TSFixMe) => (
          <div data-e2e="calendar-month-header">
            {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
            <Typography variant="h2semibold">{formatDateTimeDisplay(month, LONG_MONTH_YEAR_DISPLAY)}</Typography>
          </div>
        )}
        monthFormat={LONG_MONTH_YEAR_DISPLAY}
        hideKeyboardShortcutsPanel={true}
        daySize={48}
        initialVisibleMonth={null}
      />
    </div>
  );
};
